import { useState } from "react";
import { MapPin, PieChart, Users } from "lucide-react";
import type { Trip } from "../../types";
import { money } from "../../lib/finance";
import Avatar from "../../components/Avatar";
import type {
  CategorySpendStat,
  DestinationSpendStat,
  ProfileSpendStat,
} from "../../hooks/use-trip-stats";

export interface TripBreakdownTabsProps {
  trip: Trip;
  budgetCurrency: string;
  categoryBreakdown: CategorySpendStat[];
  destinationBreakdown: DestinationSpendStat[];
  profileBreakdown: ProfileSpendStat[];
}

export default function TripBreakdownTabs({
  trip,
  budgetCurrency,
  categoryBreakdown,
  destinationBreakdown,
  profileBreakdown,
}: TripBreakdownTabsProps) {
  const [activeTab, setActiveTab] = useState<"categories" | "destinations" | "travelers">("categories");

  return (
    <div className="trip-section">
      <div className="entry-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-pressed={activeTab === "categories"}
          onClick={() => setActiveTab("categories")}
        >
          <PieChart size={16} /> Categories
        </button>
        {(trip.destinations.length > 1 || destinationBreakdown.length > 1) && (
          <button
            type="button"
            role="tab"
            aria-pressed={activeTab === "destinations"}
            onClick={() => setActiveTab("destinations")}
          >
            <MapPin size={16} /> Destinations
          </button>
        )}
        <button
          type="button"
          role="tab"
          aria-pressed={activeTab === "travelers"}
          onClick={() => setActiveTab("travelers")}
        >
          <Users size={16} /> Travelers
        </button>
      </div>

      {activeTab === "categories" && (
        categoryBreakdown.length > 0 ? (
          <div className="budget-list">
            {categoryBreakdown.map((item) => (
              <div key={item.category} className="budget-row">
                <div className="budget-row-head">
                  <span className="budget-row-owner">
                    <strong>{item.category}</strong>
                    <span className="subtle-count">({item.count})</span>
                  </span>
                  <span className="budget-row-figures">
                    <strong>{money(item.amountBudget, budgetCurrency)}</strong>
                    <i>·</i>
                    <span>{item.percent}%</span>
                  </span>
                </div>
                <div className="budget-bar-track">
                  <div className="budget-bar-fill" style={{ width: `${item.percent}%` }} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="trip-tab-empty">
            <PieChart size={24} className="trip-tab-empty-icon" />
            <strong>No category expenses yet</strong>
            <span>Expenses you add to this trip will show category breakdown and percentages here.</span>
          </div>
        )
      )}

      {activeTab === "destinations" && (
        destinationBreakdown.length > 0 ? (
          <div className="budget-list">
            {destinationBreakdown.map((item) => (
              <div key={item.destination} className="budget-row">
                <div className="budget-row-head">
                  <span className="budget-row-owner">
                    <strong>{item.destination}</strong>
                    <span className="subtle-count">({item.count})</span>
                  </span>
                  <span className="budget-row-figures">
                    <strong>{money(item.amountBudget, budgetCurrency)}</strong>
                    <i>·</i>
                    <span>{item.percent}%</span>
                  </span>
                </div>
                <div className="budget-bar-track">
                  <div className="budget-bar-fill" style={{ width: `${item.percent}%` }} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="trip-tab-empty">
            <MapPin size={24} className="trip-tab-empty-icon" />
            <strong>No destination expenses yet</strong>
            <span>Spending tagged to country legs will appear here.</span>
          </div>
        )
      )}

      {activeTab === "travelers" && (
        profileBreakdown.length > 0 ? (
          <div className="budget-list">
            {profileBreakdown.map((item) => (
              <div key={item.owner} className="budget-row">
                <div className="budget-row-head">
                  <span className="budget-row-owner">
                    <Avatar owner={item.owner} size={18} />
                    <strong>{item.owner}</strong>
                    <span className="subtle-count">({item.count})</span>
                  </span>
                  <span className="budget-row-figures">
                    <strong>{money(item.amountBudget, budgetCurrency)}</strong>
                    <i>·</i>
                    <span>{item.percent}%</span>
                  </span>
                </div>
                <div className="budget-bar-track">
                  <div className="budget-bar-fill" style={{ width: `${item.percent}%` }} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="trip-tab-empty">
            <Users size={24} className="trip-tab-empty-icon" />
            <strong>No traveler expenses yet</strong>
            <span>Expenses logged by each traveler will break down here.</span>
          </div>
        )
      )}
    </div>
  );
}
